import { isLeFlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'

/**
 * A multi-city itinerary is persisted as one trip item per leg, all sharing a
 * `flightGroupId`. The shortlist should show each itinerary as a single card,
 * so this collapses the legs of a group into one entry while leaving one-way
 * and return options exactly where they were.
 */
export type ShortlistedFlightGroup =
  | { kind: 'single'; key: string; option: TransportOption }
  | { kind: 'multiCity'; key: string; legs: Array<TransportOption> }

function getMultiCityGroupId(option: TransportOption): string | undefined {
  const data = option.savedItemData
  if (!isLeFlightSavedItemData(data)) return undefined
  return data.flightGroupType === 'multi_city' ? data.flightGroupId : undefined
}

function getLegOrder(option: TransportOption): number {
  const data = option.savedItemData
  if (!isLeFlightSavedItemData(data)) return Number.MAX_SAFE_INTEGER
  // Legs missing an order sort last rather than jumping to the front.
  return data.legOrder ?? Number.MAX_SAFE_INTEGER
}

/**
 * @param options the flights to render, in display order
 * @param itineraryOptions every shortlisted flight on the trip. A multi-city
 * itinerary spans stops, so its legs sit on different transport connections and
 * `options` alone usually holds just one of them — the siblings are found here.
 * Defaults to `options` for callers that already hold the whole set.
 */
export function groupShortlistedFlights(
  options: Array<TransportOption>,
  itineraryOptions: Array<TransportOption> = options,
): Array<ShortlistedFlightGroup> {
  const groups: Array<ShortlistedFlightGroup> = []
  const seenGroupIds = new Set<string>()

  for (const option of options) {
    const groupId = getMultiCityGroupId(option)

    if (!groupId) {
      groups.push({ kind: 'single', key: `option-${option.itemId}`, option })
      continue
    }

    if (seenGroupIds.has(groupId)) continue
    seenGroupIds.add(groupId)

    const legs = itineraryOptions
      .filter(candidate => getMultiCityGroupId(candidate) === groupId)
      .sort((a, b) => getLegOrder(a) - getLegOrder(b))

    // A group of one is a multi-city leg whose siblings are gone. On its own it
    // is just a flight, so render it as one.
    if (legs.length < 2) {
      groups.push({ kind: 'single', key: `option-${option.itemId}`, option })
      continue
    }

    groups.push({ kind: 'multiCity', key: `group-${groupId}`, legs })
  }

  return groups
}
